<template>
  <TypeformQuestion question="Add Your Contact Info 💬"
                    center
                    max-width-class="max-w-[90vw] sm:max-w-screen-sm xl:max-w-screen-lg w-screen"
                    :loading="validating"
                    @prevStep="() => $emit('prevStep')"
                    @nextStep="() => $emit('nextStep')">

    <BaseModal :open="addPasswordModalOpen" class="z-max">
      <div>
        <BaseModalHeader v-model:open="addPasswordModalOpen">
          <LockClosedIcon class="w-4 h-4 inline-block -mt-1 mr-3 text-green-600"/>
          <span>Protect Your Application</span>
        </BaseModalHeader>
        <div class="p-4 lg:p-6 space-y-6 max-w-md">

          <p class="font-medium text-slate-600">
            You'll use this password to schedule interviews through Business Draft.
          </p>

          <FormInput v-model="store.newUserPassword.password"
                     id="password"
                     name="new-password"
                     class="w-full"
                     label="Password"
                     auto-complete="new-password"
                     type="password"
                     required/>

          <FormInput v-model="store.newUserPassword.passwordConfirmation"
                     id="password-confirmation"
                     name="new-password-confirmation"
                     class="w-full"
                     label="Confirm Password"
                     auto-complete="new-password"
                     type="password" required/>

          <FormPasswordValidator v-if="store.newUserPassword.password" grid-col-classes="grid-cols-2"
                                 :password-confirmation="store.newUserPassword.passwordConfirmation"
                                 :password="store.newUserPassword.password" class="py-4"/>

          <FormButton bold
                      id="confirmPassword"
                      color="hex"
                      size="tall"
                      class="block mx-auto"
                      :hex-background="typeformData.primaryBackground"
                      :hex-foreground="typeformData.primaryForeground"
                      :loading="validating"
                      @click="() => $emit('nextStep')">
            Confirm Password
          </FormButton>
        </div>
      </div>
    </BaseModal>

    <div class="text-left w-full max-w-[90vw]">
      <div class="gap-x-6 gap-y-8 grid grid-cols-1 lg:grid-cols-2 w-full">

        <TypeformTextInput label="First Name"
                           placeholder="e.g. Jane"
                           name="given-name"
                           autocomplete="give-name"
                           autofocus
                           v-model="store.newUserDetails.firstName"/>

        <TypeformTextInput label="Last Name"
                           placeholder="e.g. Doe"
                           name="family-name"
                           autocomplete="family-name"
                           v-model="store.newUserDetails.lastName"/>

        <div class="relative">
          <TypeformTextInput v-model="store.newUserDetails.email"
                             placeholder="Email"
                             label="Email"
                             name="user-email"
                             autocomplete="email"
                             type="email"/>

          <FormEmailCompleter
            class="absolute z-10 -top-0.5 right-0 md:max-w-fit lg:max-w-[80%] max-w-[80%] h-7 overflow-x-auto overflow-y-hidden"
            button-class="border border-white px-2 py-1 rounded-lg text-white text-xs font-medium opacity-40 hover:opacity-100"
            v-model="store.newUserDetails.email"
            :max-suggestions="3"
            disable-flex-wrap
          />

        </div>

        <TypeformTextInput v-model="store.newUserDetails.phone"
                           placeholder="Phone"
                           label="Phone (Must Receive Texts)"
                           autocomplete="tel"
                           name="user-phone"
                           format="phone"
                           type="tel"/>

        <div class="relative pb-2">
          <label class="block text-sm mb-2 opacity-60">What US city are you in?</label>
          <FormCityLatLng
            ref="cityLatLng"
            input-classes="placeholder-custom"
            :input-styles="inputStyles"
            required
            name="user-city"
            v-model="store.newUserDetails.location"
            v-model:lat="store.newUserDetails.lat"
            v-model:lng="store.newUserDetails.lng"
            v-model:city="store.newUserDetails.city"
            v-model:state="store.newUserDetails.state"
            v-model:postal-code="store.newUserDetails.postalCode"
            v-model:country="store.newUserDetails.country"
          />
          <div class="absolute bottom-0 left-0 right-0 h-0.5 transition-opacity opacity-50" :style="invertedColors"/>
        </div>

        <TypeformTextInput v-model="store.newUserDetails.username"
                           name="username"
                           placeholder="@username"
                           label="Username"
                           format="username"/>

      </div>

    </div>

  </TypeformQuestion>
</template>

<script setup>
import BaseModalHeader from "./BaseModalHeader.vue";
import BaseModal from "./BaseModal.vue";
import FormCityLatLng from "./FormCityLatLng.vue";
import FormInput from "./FormInput.vue";
import FormPasswordValidator from "./FormPasswordValidator.vue";
import TypeformQuestion from "./TypeformQuestion.vue";
import TypeformTextInput from "./TypeformTextInput.vue";
import {computed, inject, onMounted, ref} from "vue";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore.js";
import {addOpacityToHex} from "../helpers/Color";
import {
  isValidEmail,
  isValidNumber,
  isValidPassword,
  isValidPhone,
  isValidString,
  isValidUsername
} from "../helpers/ValidationRules";
import {checkIfEmailExists, checkIfUsernameTaken} from "../helpers/CommonAccountFunctions";
import {LockClosedIcon} from "@heroicons/vue/20/solid/index.js";
import FormButton from "./FormButton.vue";
import FormEmailCompleter from "./FormEmailCompleter.vue";

defineProps({validating: Boolean});
const emits = defineEmits(["prevStep", "nextStep", "update:validating", "update:validator", "update:showLogIn"]);
const store = useCandidateApplicationFormStore();

const typeformData = inject("typeformData", {});
const cityLatLng = ref(null);
const addPasswordModalOpen = ref(false);

const inputStyles = computed(() => `background:transparent;border:none;padding-left:0;padding-right:0;font-weight:medium;font-size: 1.125rem;--placeholder-color:${addOpacityToHex(typeformData.primaryForeground, 0.5)};color:${typeformData.primaryForeground};`);
const invertedColors = computed(() => `background: ${typeformData.primaryForeground};color:${typeformData.primaryBackground};`);

const createAndSearchLocation = () => {
  let location = "";

  const city = store.newUserDetails.city;
  const state = store.newUserDetails.state;

  if (city) location += city;
  if (city && state) location += ", ";
  if (state) location += state;
  if (city && state) location += ", USA";

  if (location) {
    store.newUserDetails.location = location;
    cityLatLng.value.searchAndSelectFirst(store.newUserDetails.location);
  }
};

onMounted(() => {
  store.setFurthestStep(2, "Add Your Contact Info");

  createAndSearchLocation();

  emits("update:validating", false);
  emits("update:validator", async () => {
    if (addPasswordModalOpen.value) {
      if (!isValidPassword(store.newUserPassword.password, true)) {
        toast.warning("Please enter a password.");
        addPasswordModalOpen.value = true;
        return false;
      }

      if (store.newUserPassword.password !== store.newUserPassword.passwordConfirmation) {
        toast.warning("Please make sure the passwords match");
        addPasswordModalOpen.value = true;
        return false;
      }

      return true;
    }

    if (!isValidString(store.newUserDetails.firstName, true)) {
      toast.warning("Please enter your first name.");
      return false;
    }

    if (!isValidString(store.newUserDetails.lastName, true)) {
      toast.warning("Please enter your last name.");
      return false;
    }

    if (!isValidEmail(store.newUserDetails.email, true)) {
      toast.warning("Please enter your email.");
      return false;
    }

    if (!isValidPhone(store.newUserDetails.phone, true)) {
      toast.warning("Please enter your phone number.");
      return false;
    }

    if (!isValidNumber(store.newUserDetails.lat, -90, 90) || !isValidNumber(store.newUserDetails.lng, -180, 180)) {
      toast.warning("Please select your city from the dropdown.");
      return false;
    }

    if (!isValidUsername(store.newUserDetails.username, true)) {
      toast.warning("Please enter a username.");
      return false;
    }

    let remoteChecksPassed = false;
    emits("update:validating", true);

    try {
      const emailInvalid = await checkIfEmailExists(store.newUserDetails.email);

      if (emailInvalid) {
        toast.warning("Another Business Draft account is already using this email address.");
        emits("update:showLogIn", true);
        return false;
      }

      const {
        taken,
        valid,
      } = await checkIfUsernameTaken(store.newUserDetails.username);

      if (!valid) {
        toast.warning("That username is already taken!");
        addPasswordModalOpen.value = false;
        return false;
      }

      if (taken) {
        toast.warning("Another Business Draft account is already using this username.");
        addPasswordModalOpen.value = false;
        return false;
      }

      remoteChecksPassed = true;
    } catch (e) {
      console.error(e);
    } finally {
      emits("update:validating", false);
    }


    if (!remoteChecksPassed) return false;

    addPasswordModalOpen.value = true;
    return false;
  });
});
</script>
